import React from 'react';
import clsx from 'clsx';

/**
 * Status glyph for a reservation, in the work-order glyphs' grammar: a
 * dashed ring waits on someone (pending), a solid ring is on the calendar
 * (approved), a filled check is done, a filled bar is a no-show, and a
 * filled cross is off the calendar for good (denied, cancelled, expired).
 */
export const ReservationStatusIcon: React.FC<{
  status: string;
  className?: string;
}> = ({ status, className }) => {
  const common = clsx('h-3.5 w-3.5 shrink-0', className);
  switch (status) {
    case 'pending':
      return (
        <svg viewBox="0 0 14 14" className={clsx(common, 'text-amber-400')} aria-hidden>
          <circle cx="7" cy="7" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeDasharray="2 2" />
        </svg>
      );
    case 'approved':
      return (
        <svg viewBox="0 0 14 14" className={clsx(common, 'text-cyan-400')} aria-hidden>
          <circle cx="7" cy="7" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <circle cx="7" cy="7" r="2.5" fill="currentColor" />
        </svg>
      );
    case 'completed':
      return (
        <svg viewBox="0 0 14 14" className={clsx(common, 'text-sky-400')} aria-hidden>
          <circle cx="7" cy="7" r="6" fill="currentColor" />
          <path d="M4.5 7.2 6.3 9l3.2-3.6" fill="none" stroke="#08090A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'denied':
      return (
        <svg viewBox="0 0 14 14" className={clsx(common, 'text-red-400')} aria-hidden>
          <circle cx="7" cy="7" r="6" fill="currentColor" />
          <path d="M5 5l4 4M9 5l-4 4" stroke="#08090A" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      );
    case 'no_show':
      // A booking that happened on the calendar but nobody came: the
      // completed disc, struck through in amber.
      return (
        <svg viewBox="0 0 14 14" className={clsx(common, 'text-amber-500')} aria-hidden>
          <circle cx="7" cy="7" r="6" fill="currentColor" />
          <path d="M4 7h6" stroke="#08090A" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      );
    case 'cancelled':
    case 'expired':
      return (
        <svg viewBox="0 0 14 14" className={clsx(common, 'text-zinc-500')} aria-hidden>
          <circle cx="7" cy="7" r="6" fill="currentColor" />
          <path d="M5 5l4 4M9 5l-4 4" stroke="#08090A" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 14 14" className={clsx(common, 'text-zinc-500')} aria-hidden>
          <circle cx="7" cy="7" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      );
  }
};

export default ReservationStatusIcon;
